import { FieldTypeEnum } from 'packages/sdks-next/growp'
import React, { useContext } from 'react'
import { BaseSpeacialSettingWrapper } from './BaseSpeacialSetting'
import { FileSizeSetting } from './FileSizeSetting'
import { FileNumSetting } from './FileNumSetting'
import { NumberSetting } from './NumberSetting'
import { DateSetting } from './DateSetting'
import { OptionStyleSetting } from './OptionStyleSetting'
import { OptionNumSetting } from './OptionNumSetting'
import { QuestionnaireContext } from './context'
import {
  DateTypeEnum,
  Option,
  ReqColumnWithMetaData,
  SelectTypeEnum,
} from './types'
import styled from 'styled-components'
import { ProductOptionSetting } from './ProductOptionSetting'
import { isOptionsIncludeOther } from './utils'
import { cloneDeep } from '@seiue/util'

/**
 * 题目复合表单项组件
 *
 * @param param - 参数
 * @param param.currentChild - 当前复合题目子项结构
 * @param param.childIndex - 当前复合题目子项索引
 * @returns React.ReactNode
 */
export const SelectProductSetting: React.FC<{
  currentChild: ReqColumnWithMetaData
  childIndex: number
}> = ({ currentChild, childIndex }) => {
  const { questionnaires, activeItemIndex, setQuestionnaires, structIsChange } =
    useContext(QuestionnaireContext)
  if (activeItemIndex === undefined || !questionnaires) return null
  const currentQuestion = questionnaires[activeItemIndex] || {}

  const onDateTypeChange = (checked: boolean, checkedType: DateTypeEnum) => {
    if (!checked) return
    changeChildMetaData({ dateType: checkedType })
    structIsChange && structIsChange()
  }

  const changeChildMetaData = (param: object) => {
    const newChildMetaData = {
      ...currentChild.metadata,
      ...param,
    }

    const newChild = {
      ...currentChild,
      metadata: newChildMetaData,
    }
    currentQuestion.children?.splice(childIndex, 1, newChild)

    questionnaires.splice(activeItemIndex, 1, currentQuestion)
    setQuestionnaires([...questionnaires])
  }

  const addOption = (isOther?: boolean) => {
    const options = cloneDeep(currentChild.options || [])
    const len = options.length || 0
    options.push({
      name: isOther ? '其它' : `选项${len + 1}`,
      label: isOther ? '其它' : `选项${len + 1}`,
      value: isOther ? '其它' : `选项${len + 1}`,
    })
    const newChild = { ...currentChild, options }
    const preQuestionnaire = cloneDeep(questionnaires)
    preQuestionnaire[activeItemIndex].children?.splice(childIndex, 1, newChild)
    setQuestionnaires([...preQuestionnaire])
    structIsChange && structIsChange()
  }

  const getSpeacialSetting = () => {
    const { type } = currentChild
    const {
      maxCount,
      minCount,
      fileSizeLimit,
      precision,
      dateType,
      selectType,
    } = currentChild.metadata || {}
    switch (type) {
      case FieldTypeEnum.Attachment: {
        return (
          <>
            <BaseSpeacialSettingWrapper
              isProductSetting
              title="文件大小限制"
              component={
                <FileSizeSetting
                  fileSizeLimit={fileSizeLimit}
                  onChange={(v) => {
                    changeChildMetaData({ fileSizeLimit: v })
                    structIsChange && structIsChange()
                  }}
                />
              }
            />
            <BaseSpeacialSettingWrapper
              isProductSetting
              title="上传数量限制"
              component={
                <FileNumSetting
                  isUserField={currentChild.isUserField}
                  minCount={minCount}
                  maxCount={maxCount}
                  onSelect={(param) => {
                    changeChildMetaData(param)
                    structIsChange && structIsChange()
                  }}
                />
              }
            />
          </>
        )
      }
      case FieldTypeEnum.Integer: {
        return (
          <BaseSpeacialSettingWrapper
            isProductSetting
            title="小数位数"
            component={
              <NumberSettingWrapper>
                <NumberSetting
                  isUserField={currentChild.isUserField}
                  precision={precision}
                  onSelect={(v: number) => {
                    changeChildMetaData({ precision: v })
                    structIsChange && structIsChange()
                  }}
                />
              </NumberSettingWrapper>
            }
          />
        )
      }
      case FieldTypeEnum.DateRange: {
        return (
          <BaseSpeacialSettingWrapper
            isProductSetting
            title="日期类型"
            component={
              <DateSetting
                isUserField={currentChild.isUserField}
                dateType={dateType}
                onChange={onDateTypeChange}
              />
            }
          />
        )
      }
      case FieldTypeEnum.Date:
      case FieldTypeEnum.Time: {
        return (
          <BaseSpeacialSettingWrapper
            isProductSetting
            title="时间类型"
            component={
              <DateSetting
                isUserField={currentChild.isUserField}
                dateType={dateType}
                onChange={onDateTypeChange}
              />
            }
          />
        )
      }
      case FieldTypeEnum.Single: {
        const includeOther = isOptionsIncludeOther(currentChild.options || [])
        return (
          <ProductSelectSettingWrapper>
            <BaseSpeacialSettingWrapper
              isProductSetting
              title={
                <TitleWrapper>
                  <div>选项内容</div>
                  {!currentQuestion.isUserField ? (
                    <AddWrapper>
                      <AddItem onClick={() => addOption()}>新增</AddItem>
                      {!includeOther ? (
                        <AddItem
                          onClick={() => addOption(true)}
                          style={{
                            marginLeft: '16px',
                          }}
                        >
                          其它
                        </AddItem>
                      ) : null}
                    </AddWrapper>
                  ) : null}
                </TitleWrapper>
              }
              component={
                <ProductOptionSetting
                  currentChild={currentChild}
                  childIndex={childIndex}
                  isUserField={currentChild.isUserField}
                  type={FieldTypeEnum.Single}
                  options={(currentChild.options as Option[]) || []}
                  otherRequire={currentChild.metadata?.otherRequire}
                  onChange={(checked) => {
                    currentQuestion.children?.splice(childIndex, 1, {
                      ...currentChild,
                      metadata: {
                        ...currentChild.metadata,
                        otherRequire: checked,
                      },
                    })
                    setQuestionnaires([...questionnaires])
                  }}
                />
              }
            />
            {currentChild.isUserField ? null : (
              <BaseSpeacialSettingWrapper
                isProductSetting
                title="选项样式"
                component={
                  <OptionStyleSetting
                    selectType={selectType || SelectTypeEnum.Checked}
                    onChange={changeChildMetaData}
                  />
                }
              />
            )}
          </ProductSelectSettingWrapper>
        )
      }
      case FieldTypeEnum.Multi: {
        const includeOther = isOptionsIncludeOther(currentChild.options || [])
        return (
          <ProductSelectSettingWrapper>
            <BaseSpeacialSettingWrapper
              isProductSetting
              title={
                <TitleWrapper>
                  <div>选项内容</div>
                  {!currentQuestion.isUserField ? (
                    <AddWrapper>
                      <AddItem onClick={() => addOption()}>新增</AddItem>
                      {!includeOther ? (
                        <AddItem
                          onClick={() => addOption(true)}
                          style={{
                            marginLeft: '16px',
                          }}
                        >
                          其它
                        </AddItem>
                      ) : null}
                    </AddWrapper>
                  ) : null}
                </TitleWrapper>
              }
              component={
                <ProductOptionSetting
                  currentChild={currentChild}
                  childIndex={childIndex}
                  isUserField={currentChild.isUserField}
                  type={FieldTypeEnum.Multi}
                  options={(currentChild.options as Option[]) || []}
                  otherRequire={currentChild.metadata?.otherRequire}
                  onChange={(checked) => {
                    currentQuestion.children?.splice(childIndex, 1, {
                      ...currentChild,
                      metadata: {
                        ...currentChild.metadata,
                        otherRequire: checked,
                      },
                    })
                    setQuestionnaires([...questionnaires])
                  }}
                />
              }
            />
            {currentChild.isUserField ? null : (
              <BaseSpeacialSettingWrapper
                isProductSetting
                title="选项样式"
                component={
                  <OptionStyleSetting
                    selectType={selectType || SelectTypeEnum.Checked}
                    onChange={changeChildMetaData}
                  />
                }
              />
            )}
            <BaseSpeacialSettingWrapper
              isProductSetting
              title="限制用户可选数量"
              component={
                <ProductMultiSetting>
                  <OptionNumSetting
                    isUserField={currentChild.isUserField}
                    maxCount={maxCount || 0}
                    minCount={minCount || 0}
                    optionsLen={currentChild.options?.length || 2}
                    onChange={(param) => {
                      changeChildMetaData(param)
                      structIsChange && structIsChange()
                    }}
                  />
                </ProductMultiSetting>
              }
            />
          </ProductSelectSettingWrapper>
        )
      }
    }
  }
  return <>{getSpeacialSetting()}</>
}

const NumberSettingWrapper = styled.div`
  &&& {
    .ant-select {
      width: 100%;
      height: 32px;
    }
  }
`

const ProductSelectSettingWrapper = styled.div`
  &&& {
    .ant-input {
      width: 92px;
    }
    .ant-select-selector {
      width: 236px;
    }
  }
`

const ProductMultiSetting = styled.div`
  &&& {
    .ant-select {
      width: 220px;
    }
    .ant-select-selector {
      width: 220px;
      height: 32px;
    }
  }
`

const TitleWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const AddWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const AddItem = styled.div`
  color: ${(props) => props.theme.brand._1};
  cursor: pointer;
`
